import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  TrendingUp,
  Building2,
  CreditCard,
  Plus,
  UserPlus,
  Users,
  AlertTriangle,
  ChevronRight,
  MapPin,
  RefreshCw,
  Calendar,
  ArrowRight,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../context/AuthContext";
import "./Dashboard.css";

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const formatDate = (value) => {
  if (!value) return "-";
  return dayjs(value).format("DD MMM YYYY");
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Role check: redirect basic staff (roleId 3)
  useEffect(() => {
    if (user && user.roleId === 3) {
      navigate("/list", { replace: true });
    }
  }, [user, navigate]);

  const isSuperAdmin = user?.roleId === 1;

  // Filter states
  const [filterType, setFilterType] = useState("thisMonth");
  const todayStr = dayjs().format("YYYY-MM-DD");
  const [customStartDate, setCustomStartDate] = useState(todayStr);
  const [customEndDate, setCustomEndDate] = useState(todayStr);
  const [startDateInput, setStartDateInput] = useState(todayStr);
  const [endDateInput, setEndDateInput] = useState(todayStr);

  const [branches, setBranches] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedEmployerId, setSelectedEmployerId] = useState("");

  // Dashboard stats states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalDueAmount, setTotalDueAmount] = useState(0);
  const [pendingCustomersCount, setPendingCustomersCount] = useState(0);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [inactiveCustomers, setInactiveCustomers] = useState([]);

  const [totalEmployees, setTotalEmployees] = useState(0);
  const [activeEmployees, setActiveEmployees] = useState(0);
  const [recentEmployees, setRecentEmployees] = useState([]);

  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalTrayPayment, setTotalTrayPayment] = useState(0);
  const [totalCashOnlinePayment, setTotalCashOnlinePayment] = useState(0);
  const [branchDue, setBranchDue] = useState([]);
  const [alerts, setAlerts] = useState([]);

  // Quick Add Customer Dialog State
  const [openAddModal, setOpenAddModal] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    name: "",
    mobile: "",
    location: "",
    branchId: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // Date calculation based on filterType
  const getFilterDates = useCallback(() => {
    const now = new Date();
    let start = new Date();
    let end = new Date();

    switch (filterType) {
      case "today":
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "yesterday":
        start.setDate(now.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end.setDate(now.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case "thisWeek": {
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      }
      case "thisMonth":
        start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "lastMonth":
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;
      case "custom":
        if (customStartDate && customEndDate) {
          const s = new Date(customStartDate);
          const e = new Date(customEndDate);
          if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
            start = s;
            start.setHours(0, 0, 0, 0);
            end = e;
            end.setHours(23, 59, 59, 999);
          } else {
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
          }
        }
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
    }
    return { start, end };
  }, [filterType, customStartDate, customEndDate]);

  // Initial fetch for employers if SuperAdmin
  useEffect(() => {
    if (isSuperAdmin) {
      axiosInstance
        .get("/employers")
        .then((res) => {
          const list = res?.data || [];
          setEmployers(list);
          if (list.length > 0 && !selectedEmployerId) {
            setSelectedEmployerId(list[0]._id);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch employers", err);
        });
    }
  }, [isSuperAdmin, selectedEmployerId]);

  // Fetch branches whenever employer changes
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const query = isSuperAdmin && selectedEmployerId ? `?employerId=${selectedEmployerId}` : "";
        const res = await axiosInstance.get(`/branches${query}`);
        const list = res?.data?.list || [];
        setBranches(list);
      } catch (err) {
        console.error("Failed to load branches", err);
      }
    };

    if (!isSuperAdmin || (isSuperAdmin && selectedEmployerId)) {
      fetchBranches();
    }
  }, [isSuperAdmin, selectedEmployerId]);

  // Fetch all dashboard stats
  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    if (isSuperAdmin && !selectedEmployerId) return;

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const { start, end } = getFilterDates();
      const startIso = start.toISOString();
      const endIso = end.toISOString();

      // 1. Transaction Dashboard API
      let dashboardUrl = `/transactions/dashboard?startDate=${startIso}&endDate=${endIso}`;
      if (selectedBranchId) dashboardUrl += `&branchId=${selectedBranchId}`;
      if (isSuperAdmin && selectedEmployerId) dashboardUrl += `&employerId=${selectedEmployerId}`;

      // 2. Customers List API
      let custUrl = `/customers/list?`;
      if (selectedBranchId) custUrl += `branchId=${selectedBranchId}&`;
      if (isSuperAdmin && selectedEmployerId) custUrl += `employerId=${selectedEmployerId}&`;

      // 3. Employees API
      let empUrl = `/employees`;
      if (isSuperAdmin && selectedEmployerId) empUrl += `?employerId=${selectedEmployerId}`;

      const [summaryRes, custRes, empRes] = await Promise.all([
        axiosInstance.get(dashboardUrl),
        axiosInstance.get(custUrl),
        axiosInstance.get(empUrl).catch(() => ({ data: [] })),
      ]);

      const summary = summaryRes.data?.result || summaryRes.data || {};
      const customers = custRes.data?.result || custRes.data || [];
      const employees = empRes.data?.result || empRes.data || [];

      // Process Customers
      setTotalCustomers(customers.length);
      const totalDue = customers.reduce(
        (sum, c) => sum + (Number(c.totalDueAmount) || 0),
        0
      );
      setTotalDueAmount(totalDue);
      const pendingCount = customers.filter(
        (c) => (Number(c.totalDueAmount) || 0) > 0
      ).length;
      setPendingCustomersCount(pendingCount);

      // Recent Customers (top 6 by createdAt)
      const recent = [...customers]
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        .slice(0, 6);
      setRecentCustomers(recent);

      // Inactive Customers (> 30 days since last transaction)
      const inactive = [...customers]
        .filter((c) => {
          const lastTx = c.lastTransactionDate;
          const diffDays = lastTx
            ? Math.floor(
                (new Date().getTime() - new Date(lastTx).getTime()) /
                  (1000 * 60 * 60 * 24)
              )
            : 999;
          return diffDays > 30;
        })
        .sort((a, b) => {
          if (!a.lastTransactionDate && !b.lastTransactionDate) return 0;
          if (!a.lastTransactionDate) return -1;
          if (!b.lastTransactionDate) return 1;
          const diffA = Math.floor(
            (new Date().getTime() - new Date(a.lastTransactionDate).getTime()) /
              (1000 * 60 * 60 * 24)
          );
          const diffB = Math.floor(
            (new Date().getTime() - new Date(b.lastTransactionDate).getTime()) /
              (1000 * 60 * 60 * 24)
          );
          return diffB - diffA;
        })
        .slice(0, 6);
      setInactiveCustomers(inactive);

      // Process Employees
      const empList = Array.isArray(employees) ? employees : [];
      setTotalEmployees(empList.length);
      const activeCount = empList.filter(
        (e) => e.status === "active" || e.status === true
      ).length;
      setActiveEmployees(activeCount || empList.length);
      const recentEmp = [...empList]
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        .slice(0, 6);
      setRecentEmployees(recentEmp);

      // Process Metrics from Transaction Summary
      setTotalRevenue(Number(summary.overview?.totalSales || 0));
      setTotalTrayPayment(Number(summary.overview?.trayCreditAmount || 0));
      setTotalCashOnlinePayment(Number(summary.overview?.totalPayments || 0));

      // Branch Due Breakdown
      if (Array.isArray(summary.branchPerformance)) {
        setBranchDue(
          summary.branchPerformance.map((b) => ({
            branchName: b.branchName,
            dueAmount: Number(b.dueAmount || 0),
            customers: b.customers || 0,
            totalBusiness: Number(b.totalBusiness || 0),
          }))
        );
      } else {
        setBranchDue([]);
      }

      // Dynamic Alerts for high outstanding balances (> ₹10,000)
      const dynamicAlerts = (summary.topDueCustomers || [])
        .filter((c) => Number(c.dueAmount || 0) > 10000)
        .map((c) => ({
          name: c.name,
          dueAmount: Number(c.dueAmount || 0),
          id: c.customerId,
        }));
      setAlerts(dynamicAlerts);
    } catch (err) {
      console.error("Dashboard data load error:", err);
      setError(err?.response?.data?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    isSuperAdmin,
    selectedEmployerId,
    selectedBranchId,
    getFilterDates,
  ]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Handle Quick Add Customer Submit
  const handleAddCustomerSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!customerForm.name.trim()) errors.name = "Customer name is required";
    if (!customerForm.mobile.trim()) {
      errors.mobile = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(customerForm.mobile.trim())) {
      errors.mobile = "Enter a valid 10-digit mobile number";
    }
    if (!customerForm.location.trim()) errors.location = "Location is required";
    if (!customerForm.branchId) errors.branchId = "Please select a branch";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSavingCustomer(true);
      const payload = {
        name: customerForm.name.trim(),
        mobile: customerForm.mobile.trim(),
        location: customerForm.location.trim(),
        branchId: customerForm.branchId,
      };
      if (isSuperAdmin && selectedEmployerId) {
        payload.employerId = selectedEmployerId;
      }

      const res = await axiosInstance.post("/customers/create", payload);
      toast.success(res.data?.message || "Customer created successfully!");
      setOpenAddModal(false);
      setCustomerForm({
        name: "",
        mobile: "",
        location: "",
        branchId: "",
      });
      setFormErrors({});
      fetchDashboardData(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create customer");
    } finally {
      setSavingCustomer(false);
    }
  };

  const todayFormatted = dayjs().format("dddd, D MMM YYYY");

  return (
    <Box className="web-dashboard-root">
      {/* 1. Header Welcome Gradient Banner */}
      <Box className="dashboard-hero-banner">
        <Box className="hero-banner-content">
          <Box className="hero-banner-text">
            <Typography className="hero-banner-date">
              <Calendar size={14} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} />
              {todayFormatted}
            </Typography>
            <Typography variant="h4" className="hero-banner-greeting">
              Welcome back, <span className="hero-banner-name">{user?.name || "Owner"}</span>
            </Typography>
          </Box>

          <Box className="hero-banner-actions">
            <Tooltip title="Reload Dashboard Stats">
              <Button
                variant="contained"
                className="hero-refresh-btn"
                onClick={() => fetchDashboardData(true)}
                disabled={loading || refreshing}
                startIcon={
                  <RefreshCw
                    size={16}
                    className={refreshing ? "spin-animation" : ""}
                  />
                }
              >
                {refreshing ? "Refreshing..." : "Refresh"}
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Main Container */}
      <Box className="dashboard-inner-container">
        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            className="dashboard-alert-banner"
            action={
              <Button color="inherit" size="small" onClick={() => fetchDashboardData()}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* 2. Hero Card: Total Customer Dues */}
        <Paper className="hero-dues-card" elevation={2}>
          <Box className="hero-dues-left">
            <Typography className="hero-dues-label">Total Customer Dues</Typography>
            <Typography className="hero-dues-value">
              {formatCurrency(totalDueAmount)}
            </Typography>
            <Typography className="hero-dues-footer">
              from <strong style={{ color: "#0f172a" }}>{pendingCustomersCount}</strong> active customer balances
            </Typography>
          </Box>
          <Box className="hero-dues-right">
            <Button
              variant="outlined"
              className="hero-view-customers-btn"
              onClick={() => navigate("/list")}
              endIcon={<ArrowRight size={16} />}
            >
              View Customers
            </Button>
          </Box>
        </Paper>

        {/* 3. Important Alerts: Overdue Customers */}
        {alerts && alerts.length > 0 && (
          <Box className="dashboard-alerts-container">
            <Box className="alerts-header-row">
              <Box className="alerts-pulse-indicator" />
              <Typography className="alerts-section-title">
                Overdue Customers (Top {alerts.length})
              </Typography>
            </Box>
            <Box className="alerts-list-grid">
              {alerts.map((item, idx) => {
                const initials = item?.name ? item.name.charAt(0).toUpperCase() : "?";
                return (
                  <Box
                    key={idx}
                    className="alert-customer-card"
                    onClick={() => item.id && navigate(`/customer-details/${item.id}`)}
                  >
                    <Box className="alert-avatar-circle">
                      <Typography className="alert-avatar-text">{initials}</Typography>
                    </Box>
                    <Box className="alert-customer-info">
                      <Typography className="alert-customer-name" noWrap>
                        {item.name}
                      </Typography>
                      <Typography className="alert-customer-due">
                        {formatCurrency(item.dueAmount)}
                      </Typography>
                    </Box>
                    <ChevronRight size={18} className="alert-chevron-icon" />
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* 4. Key Metrics Section Header & Filter Controls */}
        <Box className="section-header-row">
          <Box className="section-title-group">
            <Typography variant="h6" className="dashboard-section-title">
              Key Operations
            </Typography>
            <Typography className="dashboard-section-subtitle">
              Overall business sales, tray payments and receivables
            </Typography>
          </Box>

          <Box className="dashboard-filter-selectors">
            {isSuperAdmin && (
              <FormControl size="small" className="filter-select-control">
                <InputLabel>Employer</InputLabel>
                <Select
                  label="Employer"
                  value={selectedEmployerId}
                  onChange={(e) => {
                    setSelectedEmployerId(e.target.value);
                    setSelectedBranchId("");
                  }}
                >
                  {employers.map((emp) => (
                    <MenuItem key={emp._id} value={emp._id}>
                      {emp.employerName || emp.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl size="small" className="filter-select-control">
              <InputLabel>Store / Branch</InputLabel>
              <Select
                label="Store / Branch"
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value)}
              >
                <MenuItem value="">All Stores</MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Timeframe Filter Pills */}
        <Paper className="timeframe-filter-panel" elevation={0}>
          <Box className="filter-pills-row">
            {[
              { label: "Today", value: "today" },
              { label: "Yesterday", value: "yesterday" },
              { label: "This Week", value: "thisWeek" },
              { label: "This Month", value: "thisMonth" },
              { label: "Last Month", value: "lastMonth" },
              { label: "Custom Range", value: "custom" },
            ].map((pill) => {
              const active = filterType === pill.value;
              return (
                <button
                  key={pill.value}
                  type="button"
                  className={`filter-pill-button ${active ? "pill-active" : ""}`}
                  onClick={() => setFilterType(pill.value)}
                >
                  {pill.label}
                </button>
              );
            })}
          </Box>

          {/* Custom Date Range Row */}
          {filterType === "custom" && (
            <Box className="custom-date-row">
              <Box className="custom-date-item">
                <Typography className="custom-date-label">Start Date</Typography>
                <input
                  type="date"
                  className="custom-date-input"
                  value={startDateInput}
                  onChange={(e) => setStartDateInput(e.target.value)}
                />
              </Box>
              <Box className="custom-date-item">
                <Typography className="custom-date-label">End Date</Typography>
                <input
                  type="date"
                  className="custom-date-input"
                  value={endDateInput}
                  onChange={(e) => setEndDateInput(e.target.value)}
                />
              </Box>
              <Button
                variant="contained"
                className="custom-date-apply-btn"
                onClick={() => {
                  setCustomStartDate(startDateInput);
                  setCustomEndDate(endDateInput);
                  fetchDashboardData(true);
                }}
              >
                Apply Range
              </Button>
            </Box>
          )}
        </Paper>

        {/* 5. Metrics 4-Grid Cards */}
        {loading && !refreshing ? (
          <Box className="dashboard-loading-center">
            <CircularProgress size={42} style={{ color: "var(--primary-color, #4f46e5)" }} />
            <Typography className="dashboard-loading-text">
              Retrieving business stats...
            </Typography>
          </Box>
        ) : (
          <>
            <Box className="metrics-four-grid">
              {/* Card 1: Total Sales */}
              <Paper className="metric-card" elevation={1}>
                <Box className="metric-card-top">
                  <Box className="metric-icon-wrap" style={{ backgroundColor: "var(--primary-light, #eef2ff)" }}>
                    <TrendingUp size={22} color="var(--primary-color, #4f46e5)" />
                  </Box>
                  <Typography className="metric-label">Total Sales</Typography>
                </Box>
                <Typography className="metric-value">{formatCurrency(totalRevenue)}</Typography>
                <Typography className="metric-footer">For selected range</Typography>
              </Paper>

              {/* Card 2: Total Tray Payment */}
              <Paper className="metric-card" elevation={1}>
                <Box className="metric-card-top">
                  <Box className="metric-icon-wrap" style={{ backgroundColor: "#fff7ed" }}>
                    <Building2 size={22} color="#ea580c" />
                  </Box>
                  <Typography className="metric-label">Total Tray Payment</Typography>
                </Box>
                <Typography className="metric-value">{formatCurrency(totalTrayPayment)}</Typography>
                <Typography className="metric-footer">Tray credit value</Typography>
              </Paper>

              {/* Card 3: Cash / Online Payment */}
              <Paper className="metric-card" elevation={1}>
                <Box className="metric-card-top">
                  <Box className="metric-icon-wrap" style={{ backgroundColor: "#ecfdf5" }}>
                    <CreditCard size={22} color="#10b981" />
                  </Box>
                  <Typography className="metric-label">Cash / Online</Typography>
                </Box>
                <Typography className="metric-value">{formatCurrency(totalCashOnlinePayment)}</Typography>
                <Typography className="metric-footer">Received payments</Typography>
              </Paper>

              {/* Card 4: New Customer Quick Add */}
              <Paper
                className="metric-card metric-action-card"
                elevation={1}
                onClick={() => {
                  setCustomerForm({
                    name: "",
                    mobile: "",
                    location: "",
                    branchId: selectedBranchId || (branches[0]?._id || ""),
                  });
                  setFormErrors({});
                  setOpenAddModal(true);
                }}
              >
                <Box className="metric-card-top">
                  <Box className="metric-icon-wrap action-icon-wrap">
                    <Plus size={22} color="var(--primary-contrast, #ffffff)" />
                  </Box>
                  <Typography className="metric-label action-label">New Customer</Typography>
                </Box>
                <Typography className="metric-value action-value">+ Quick Add</Typography>
                <Typography className="metric-footer action-footer">Click to add customer</Typography>
              </Paper>
            </Box>

            {/* Inactive & New Customers Sections Side by Side */}
            <Box className="dashboard-split-row">
              {/* Inactive Customers Card List */}
              <Paper className="dashboard-card-section" elevation={1}>
                <Box className="card-section-header">
                  <Box>
                    <Typography className="section-heading-text">Inactive Customers</Typography>
                    <Typography className="section-sub-text">
                      No transactions in over 30 days
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    className="view-all-link-btn"
                    onClick={() => navigate("/list?filter=inactive")}
                  >
                    View All
                  </Button>
                </Box>

                {inactiveCustomers.length > 0 ? (
                  <Box className="customer-cards-grid">
                    {inactiveCustomers.map((cust) => {
                      const lastTx = cust.lastTransactionDate;
                      const diffDays = lastTx
                        ? Math.floor(
                            (new Date().getTime() - new Date(lastTx).getTime()) /
                              (1000 * 60 * 60 * 24)
                          )
                        : 999;
                      const diffText = diffDays === 999 ? "Never" : `${diffDays}d inactive`;

                      return (
                        <Box
                          key={cust._id}
                          className="customer-mini-card inactive-border"
                          onClick={() => navigate(`/customer-details/${cust._id}`)}
                        >
                          <Box className="avatar-chip inactive-avatar">
                            {cust.name ? cust.name.charAt(0).toUpperCase() : "C"}
                          </Box>
                          <Typography className="customer-mini-name" noWrap>
                            {cust.name}
                          </Typography>
                          <Typography className="customer-mini-location" noWrap>
                            <MapPin size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 2 }} />
                            {cust.location || "No location"}
                          </Typography>
                          <Box className="inactive-tag-chip">{diffText}</Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography className="empty-section-message">
                    No inactive customers found.
                  </Typography>
                )}
              </Paper>

              {/* New / Recent Customers Card List */}
              <Paper className="dashboard-card-section" elevation={1}>
                <Box className="card-section-header">
                  <Box>
                    <Typography className="section-heading-text">New Customers</Typography>
                    <Typography className="section-sub-text">Recently added accounts</Typography>
                  </Box>
                  <Button
                    size="small"
                    className="view-all-link-btn"
                    onClick={() => navigate("/list")}
                  >
                    View All
                  </Button>
                </Box>

                {recentCustomers.length > 0 ? (
                  <Box className="customer-cards-grid">
                    {recentCustomers.map((cust) => (
                      <Box
                        key={cust._id}
                        className="customer-mini-card"
                        onClick={() => navigate(`/customer-details/${cust._id}`)}
                      >
                        <Box className="avatar-chip standard-avatar">
                          {cust.name ? cust.name.charAt(0).toUpperCase() : "C"}
                        </Box>
                        <Typography className="customer-mini-name" noWrap>
                          {cust.name}
                        </Typography>
                        <Typography className="customer-mini-location" noWrap>
                          <MapPin size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 2 }} />
                          {cust.location || "No location"}
                        </Typography>
                        <Typography
                          className={`customer-mini-due ${
                            Number(cust.totalDueAmount || 0) > 0 ? "due-red" : "due-green"
                          }`}
                        >
                          {formatCurrency(cust.totalDueAmount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography className="empty-section-message">
                    No recent customers found.
                  </Typography>
                )}
              </Paper>
            </Box>

            {/* Dues by Store & Registered Staff Grid */}
            <Box className="dashboard-split-row">
              {/* Dues by Store */}
              <Paper className="dashboard-card-section" elevation={1}>
                <Box className="card-section-header">
                  <Box>
                    <Typography className="section-heading-text">Dues by Store</Typography>
                    <Typography className="section-sub-text">
                      Outstanding balance per branch
                    </Typography>
                  </Box>
                </Box>

                {branchDue.length > 0 ? (
                  <Box className="branch-cards-grid">
                    {branchDue.map((b, idx) => (
                      <Box key={idx} className="branch-stat-card">
                        <Box className="branch-card-header">
                          <Building2 size={18} color="#64748b" />
                          <Typography className="branch-stat-name" noWrap>
                            {b.branchName}
                          </Typography>
                        </Box>
                        <Typography className="branch-stat-due">
                          {formatCurrency(b.dueAmount)}
                        </Typography>
                        <Typography className="branch-stat-footer">Outstanding due</Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography className="empty-section-message">
                    No branch dues data available.
                  </Typography>
                )}
              </Paper>

              {/* Registered Staff */}
              <Paper className="dashboard-card-section" elevation={1}>
                <Box className="card-section-header">
                  <Box>
                    <Typography className="section-heading-text">Registered Staff</Typography>
                    <Typography className="section-sub-text">
                      Active employees and staff list
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    className="view-all-link-btn"
                    onClick={() => navigate("/employees")}
                  >
                    Staff List
                  </Button>
                </Box>

                {recentEmployees.length > 0 ? (
                  <Box className="staff-cards-grid">
                    {recentEmployees.map((emp, idx) => (
                      <Box key={idx} className="staff-mini-card">
                        <Box className="staff-avatar-chip">
                          {emp.fullName
                            ? emp.fullName.charAt(0).toUpperCase()
                            : emp.name
                            ? emp.name.charAt(0).toUpperCase()
                            : "E"}
                        </Box>
                        <Typography className="staff-card-name" noWrap>
                          {emp.fullName || emp.name}
                        </Typography>
                        <Typography className="staff-card-role" noWrap>
                          {emp.roleName || "Staff"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography className="empty-section-message">
                    No registered staff found.
                  </Typography>
                )}
              </Paper>
            </Box>

          </>
        )}
      </Box>

      {/* Quick Add Customer Dialog */}
      <Dialog
        open={openAddModal}
        onClose={() => !savingCustomer && setOpenAddModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="modal-title-custom">
          <UserPlus size={20} style={{ marginRight: 8, verticalAlign: "middle" }} />
          Add New Customer
        </DialogTitle>
        <form onSubmit={handleAddCustomerSubmit} noValidate>
          <DialogContent>
            <Box display="grid" gap={2} mt={1}>
              <TextField
                label="Customer Name"
                value={customerForm.name}
                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                fullWidth
                required
                error={!!formErrors.name}
                helperText={formErrors.name}
                autoFocus
              />

              <TextField
                label="Mobile Number"
                value={customerForm.mobile}
                onChange={(e) => setCustomerForm({ ...customerForm, mobile: e.target.value })}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">🇮🇳 +91</InputAdornment>,
                  inputMode: "numeric",
                }}
                error={!!formErrors.mobile}
                helperText={formErrors.mobile}
              />

              <TextField
                label="Location"
                value={customerForm.location}
                onChange={(e) => setCustomerForm({ ...customerForm, location: e.target.value })}
                fullWidth
                required
                error={!!formErrors.location}
                helperText={formErrors.location}
              />

              <FormControl fullWidth required error={!!formErrors.branchId}>
                <InputLabel>Branch</InputLabel>
                <Select
                  label="Branch"
                  value={customerForm.branchId}
                  onChange={(e) => setCustomerForm({ ...customerForm, branchId: e.target.value })}
                >
                  {branches.map((b) => (
                    <MenuItem key={b._id} value={b._id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.branchId && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {formErrors.branchId}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions style={{ padding: "16px 24px" }}>
            <Button
              onClick={() => setOpenAddModal(false)}
              disabled={savingCustomer}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={savingCustomer}
              style={{ backgroundColor: "var(--primary-color, #4f46e5)", color: "var(--primary-contrast, #ffffff)" }}
              startIcon={savingCustomer ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {savingCustomer ? "Saving..." : "Save Customer"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
